import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';
import { useMemo, useState } from 'react';
import { isBoolean, isEmpty } from 'mojito/utils';
import EventListMarketsSection from 'modules/event-list/event-list-markets-section.js';
import EventListUtils from 'modules/event-list/utils';
import EarlyPayoutOffers from 'modules/event-list/early-payout-offers/index.js';

const { Button, Text, FlexPane, RotatableImage, HideablePane } = MojitoPresentation.Components;
const { uiStyle } = MojitoCore.Presentation;
const isExpanded = (expanded, initiallyExpanded) =>
    isBoolean(expanded) ? expanded : initiallyExpanded;

export default function EventListGroup(props) {
    const {
        mojitoTools: { config, appContext },
        children,
        eventGroup,
        sportId,
        initiallyExpanded,
        onExpandChange,
        showEarlyPayoutOffers,
    } = props;
    const { emitAnalytics } = appContext.analyticsEmitter;
    const [expanded, setExpanded] = useState();

    const onHeaderClick = () => {
        setExpanded(!isExpanded(expanded, initiallyExpanded));
        onExpandChange(!expanded, eventGroup.id);
        emitAnalytics('competitionToggleExpandable', {
            competitionName: eventGroup.canonicalTypeName,
            sport: sportId,
        });
    };

    return (
        <FlexPane class="ta-EventListGroup" config={config.container}>
            {config.showOffersAboveHeader && showEarlyPayoutOffers && (
                <EarlyPayoutOffers config={config.earlyPayoutOffers} eventGroup={eventGroup} />
            )}
            <Header
                {...props}
                expanded={isExpanded(expanded, initiallyExpanded)}
                onClick={onHeaderClick}
            />
            <HideablePane
                hidden={!isExpanded(expanded, initiallyExpanded)}
                config={config.contentHideablePane}
            >
                {!config.showOffersAboveHeader && showEarlyPayoutOffers && (
                    <EarlyPayoutOffers config={config.earlyPayoutOffers} eventGroup={eventGroup} />
                )}
                {children}
            </HideablePane>
        </FlexPane>
    );
}

const Header = props => {
    const {
        onClick,
        eventGroup,
        headerTypes,
        expanded,
        sportId,
        isInPlay,
        isTimeSorting,
        hideMarketHeaders,
        mojitoTools: { config, stringResolver },
    } = props;
    const { showDuoLineHeader } = config;
    const headerButtonConfig = useHeaderButtonConfig(config, sportId, isInPlay);
    const { primaryText, secondaryText } = config.showHeaderLabels
        ? resolveHeaderLabels(showDuoLineHeader, eventGroup, stringResolver)
        : {};

    const hideHeader = !config.showHeaderLabels && (!headerTypes.length || hideMarketHeaders);

    if (hideHeader) {
        return;
    }

    return (
        <Button
            class={'ta-GroupHeader'}
            onClick={onClick}
            config={headerButtonConfig}
            disabled={!config.expandable || isTimeSorting}
        >
            {!isTimeSorting && (primaryText || secondaryText) && (
                <FlexPane config={config.headerInfoContainer}>
                    {config.expandable && (
                        <RotatableImage
                            config={config.rotation}
                            angle={expanded ? '0deg' : '180deg'}
                        />
                    )}
                    <FlexPane
                        class={'ta-headerTypeAndClassContainer'}
                        config={config.headerTypeAndClassContainer}
                    >
                        {primaryText && (
                            <Text config={config.headerPrimaryLabel}>{primaryText}</Text>
                        )}
                        {secondaryText && (
                            <Text config={config.headerSecondaryLabel}>{secondaryText}</Text>
                        )}
                    </FlexPane>
                </FlexPane>
            )}
            {expanded && !hideMarketHeaders && (
                <EventListMarketsSection config={config.headerSegmentsContainer} sportId={sportId}>
                    {headerTypes.map((headerSegment, index) => (
                        <HeaderSegment
                            /* eslint-disable-next-line react/no-array-index-key */
                            key={index} // NOSONAR - Index is used as a simple and static key for rendering
                            config={config}
                            headerTypes={headerSegment}
                            sportId={sportId}
                            stringResolver={stringResolver}
                        />
                    ))}
                </EventListMarketsSection>
            )}
        </Button>
    );
};

// A header can be told apart by the sport it lists, and a live one by being live. Live is the louder
// of the two and hence wins, leaving every live group looking alike no matter the sport.
const useHeaderButtonConfig = (config, sportId, isInPlay) =>
    useMemo(() => {
        const { headerButton, headerButtonBySport, headerButtonInPlay } = config;
        const overrideBase = isInPlay
            ? headerButtonInPlay?.style?.base
            : headerButtonBySport?.[sportId]?.style?.base;
        return isEmpty(overrideBase)
            ? headerButton
            : uiStyle.merge(headerButton, { style: { base: overrideBase } });
    }, [config, sportId, isInPlay]);

const HeaderSegment = ({ headerTypes = [], sportId, config, stringResolver }) => {
    return (
        <FlexPane config={config.headerSegment}>
            {headerTypes.map(headerType => (
                <Text
                    key={headerType}
                    class={`ta-ColumnItem ta-HeaderType-${headerType}`}
                    config={config.headerSegmentLabel}
                >
                    {resolveHeaderTypeString(headerType, sportId, stringResolver)}
                </Text>
            ))}
        </FlexPane>
    );
};

const resolveHeaderLabels = (showDuoLineHeader, eventGroup, stringResolver) => {
    const { className, typeName } = eventGroup;
    return showDuoLineHeader
        ? {
              primaryText: stringResolver.resolveAndFormatString(
                  '$EVENT_LIST_GROUP.TYPE_HEADER_FORMAT',
                  typeName
              ),
              secondaryText: stringResolver.resolveAndFormatString(
                  '$EVENT_LIST_GROUP.CLASS_HEADER_FORMAT',
                  className
              ),
          }
        : {
              primaryText: stringResolver.resolveAndFormatString(
                  '$EVENT_LIST_GROUP.HEADER_FORMAT',
                  className,
                  typeName
              ),
          };
};

const resolveHeaderTypeString = (headerType, sportId, stringResolver) => {
    // Each headerType might have overrides per sport so the valid keys could be e.g.
    // EVENT_LIST_GROUP.HEADER_TYPES.Over or EVENT_LIST_GROUP.HEADER_TYPES.Over.tennis or EVENT_LIST_GROUP.HEADER_TYPES.DEFAULT.
    const l10nBaseKey = `$EVENT_LIST_GROUP.HEADER_TYPES.${headerType}`;
    return (
        EventListUtils.resolveComplexTranslationString(l10nBaseKey, sportId, stringResolver) ||
        headerType
    );
};
